import { Box, Badge } from '@palmetto/palmetto-components';
import { Link } from 'react-router-dom';

interface BillingBlockResultProps {
  results: {
    completedAccounts?: string[];
    invalidAccounts?: { accountId: string; reason: string }[];
    accountsAlreadyBlocked?: string[];
    accountsWithNoBlocks?: string[];
    accountsWithFirstPaymentDueDate?: string[];
    accountsAlreadyInClosedFund?: string[];
  };
  reasonLabel?: string;
}

export function BillingBlockResult({ results, reasonLabel }: BillingBlockResultProps) {
  const {
    completedAccounts,
    invalidAccounts,
    accountsAlreadyBlocked,
    accountsWithNoBlocks,
    accountsWithFirstPaymentDueDate,
    accountsAlreadyInClosedFund,
  } = results;

  return (
    <Box padding="lg" background="white" borderRadius="md" shadow="sm">
      <Box childGap="md">
        <Box fontSize="lg" fontWeight="bold">
          Results
        </Box>

        {completedAccounts && completedAccounts.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="success" message={completedAccounts.length.toString()} />
              <Box fontWeight="bold">Successfully Processed</Box>
            </Box>
            <Box childGap="xs">
              {completedAccounts.map((accountId) => (
                <Box key={accountId}>
                  <Link to={`/accounts/${accountId}`}>{accountId}</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {invalidAccounts && invalidAccounts.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="danger" message={invalidAccounts.length.toString()} />
              <Box fontWeight="bold">Invalid Accounts</Box>
            </Box>
            <Box childGap="xs">
              {invalidAccounts.map((item) => (
                <Box key={item.accountId} display="flex" gap="sm">
                  <Box>{item.accountId}</Box>
                  <Box color="grey-500">- {item.reason}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {accountsAlreadyBlocked && accountsAlreadyBlocked.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="warning" message={accountsAlreadyBlocked.length.toString()} />
              <Box fontWeight="bold">Already Blocked{reasonLabel ? ` - ${reasonLabel}` : ''}</Box>
            </Box>
            <Box childGap="xs">
              {accountsAlreadyBlocked.map((accountId) => (
                <Box key={accountId}>
                  <Link to={`/accounts/${accountId}`}>{accountId}</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {accountsWithNoBlocks && accountsWithNoBlocks.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="warning" message={accountsWithNoBlocks.length.toString()} />
              <Box fontWeight="bold">
                {reasonLabel ? `No blocks found with selected reason: ${reasonLabel}` : 'No Blocks Found'}
              </Box>
            </Box>
            <Box childGap="xs">
              {accountsWithNoBlocks.map((accountId) => (
                <Box key={accountId}>
                  <Link to={`/accounts/${accountId}`}>{accountId}</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {accountsWithFirstPaymentDueDate && accountsWithFirstPaymentDueDate.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="danger" message={accountsWithFirstPaymentDueDate.length.toString()} />
              <Box fontWeight="bold">Cannot Block - Already sent to billing</Box>
            </Box>
            <Box childGap="xs">
              {accountsWithFirstPaymentDueDate.map((accountId) => (
                <Box key={accountId}>
                  <Link to={`/accounts/${accountId}`}>{accountId}</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {accountsAlreadyInClosedFund && accountsAlreadyInClosedFund.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap="sm" margin="0 0 sm 0">
              <Badge variant="danger" message={accountsAlreadyInClosedFund.length.toString()} />
              <Box fontWeight="bold">Cannot Block - Account in closed fund</Box>
            </Box>
            <Box childGap="xs">
              {accountsAlreadyInClosedFund.map((accountId: string) => (
                <Box key={accountId}>
                  <Link to={`/accounts/${accountId}`}>{accountId}</Link>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
